import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiBriefcase,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiFilter,
  FiX,
} from 'react-icons/fi';
import { lraService } from '../../services/apiService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatCurrency } from '../../utils/formatters';

const BusinessDirectory = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [division, setDivision] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;

  const {
    data,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery(
    ['lra-businesses', { search, division, status, page, limit }],
    () =>
      lraService.listBusinesses({
        search,
        division,
        status,
        page,
        limit,
      }),
    { keepPreviousData: true }
  );

  const businesses = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination.total_pages || 1;
  const total = pagination.total || 0;

  const clearFilters = () => {
    setSearch('');
    setDivision('');
    setStatus('');
    setPage(1);
  };

  const hasFilters = search || division || status;

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };
  const handleNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  if (isLoading) return <LoadingSpinner message="Loading businesses..." />;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Business Directory</h1>
          <p className="page-subtitle">
            {total} registered {total === 1 ? 'business' : 'businesses'} in
            the system
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          <FiFilter size={16} color="#6b7280" />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Filters
          </span>
          {hasFilters && (
            <button
              className="btn btn-secondary"
              onClick={clearFilters}
              style={{ padding: '4px 10px', fontSize: '12px', marginLeft: 'auto' }}
            >
              <FiX size={14} />
              Clear
            </button>
          )}
        </div>

        <div className="form-row">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div className="search-bar" style={{ marginBottom: 0 }}>
              <FiSearch size={20} />
              <input
                type="text"
                placeholder="Search by business name, TIN, or DSSN..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={division}
              onChange={(e) => {
                setDivision(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Divisions</option>
              <option value="large">Large Tax</option>
              <option value="medium">Medium Tax</option>
              <option value="small_micro">Small & Micro</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <select
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Statuses</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {businesses.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiBriefcase size={48} />
            <h3>No businesses found</h3>
            <p>
              {hasFilters
                ? 'Try adjusting your search or filters'
                : 'No businesses are registered yet'}
            </p>
            {hasFilters && (
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>TIN</th>
                  <th>Division</th>
                  <th>Status</th>
                  <th>Sales (30d)</th>
                  <th>Filings</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((b) => (
                  <tr key={b.dssn}>
                    <td>
                      <div>
                        <strong>{b.business_name}</strong>
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#9ca3af',
                            fontFamily: 'monospace',
                            marginTop: 2,
                          }}
                        >
                          {b.dssn}
                        </div>
                      </div>
                    </td>
                    <td>
                      <code style={{ fontSize: '12px' }}>
                        {b.tin || '—'}
                      </code>
                    </td>
                    <td style={{ textTransform: 'capitalize' }}>
                      {(b.tax_division || 'unclassified').replace(/_/g, ' ')}
                    </td>
                    <td>
                      <StatusBadge status={b.verification_status} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {formatCurrency(b.sales_30d_amount || 0)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {b.sales_30d_count || 0} txns
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          background:
                            b.filings_count > 0 ? '#dcfce7' : '#f3f4f6',
                          color:
                            b.filings_count > 0 ? '#166534' : '#6b7280',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {b.filings_count || 0}
                      </span>
                    </td>
                    <td>
                      <button
                        className="icon-btn"
                        onClick={() =>
                          navigate(`/lra/businesses/${b.dssn}`)
                        }
                        title="View profile"
                      >
                        <FiEye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                padding: '12px 16px',
                background: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                Page {page} of {totalPages} • {total} total
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePrev}
                  disabled={page === 1}
                >
                  <FiChevronLeft size={16} />
                  Previous
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleNext}
                  disabled={page >= totalPages}
                >
                  Next
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Helper ----------

const StatusBadge = ({ status }) => {
  const colors = {
    verified: { bg: '#dcfce7', color: '#166534', label: 'Verified' },
    pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  };
  const c = colors[status] || {
    bg: '#f3f4f6',
    color: '#6b7280',
    label: status || 'Unknown',
  };
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      {c.label}
    </span>
  );
};

export default BusinessDirectory;
